import React, { useEffect, useState } from 'react';
import { Prospect } from '../../shared/types';
import { fashionProducts, FashionProduct } from '../../shared/data/ecommerceData';
import { ShoppingBag, Heart, Search, Filter, Sparkles, X, Plus, Minus, ChevronRight, CheckCircle2, ArrowRight } from 'lucide-react';

interface EcommerceDemoProps {
  prospect: Prospect;
}

export const EcommerceDemo: React.FC<EcommerceDemoProps> = ({ prospect }) => {
  const [activeCategory, setActiveCategory] = useState<'todos' | 'lancamentos' | 'vestuario' | 'calcados' | 'acessorios'>('todos');
  const [selectedProduct, setSelectedProduct] = useState<FashionProduct | null>(null);
  const [selectedSize, setSelectedSize] = useState<string>('');
  const [cart, setCart] = useState<{ item: FashionProduct; size: string; quantity: number }[]>([]);
  const [isCartOpen, setIsCartOpen] = useState(false);

  // Provador Virtual State
  const [height, setHeight] = useState(168);
  const [weight, setWeight] = useState(62);
  const [recommendedSize, setRecommendedSize] = useState('M');

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const calculateFitting = (h: number, w: number) => {
    setHeight(h);
    setWeight(w);
    if (w < 55) setRecommendedSize('P');
    else if (w < 70) setRecommendedSize('M');
    else setRecommendedSize('G');
  };

  const filteredProducts = activeCategory === 'todos' 
    ? fashionProducts 
    : fashionProducts.filter(p => p.category === activeCategory);

  const addToCart = (product: FashionProduct) => {
    const sizeToUse = selectedSize || product.sizes[0];
    setCart(prev => {
      const existing = prev.find(c => c.item.id === product.id && c.size === sizeToUse);
      if (existing) {
        return prev.map(c => (c.item.id === product.id && c.size === sizeToUse) ? { ...c, quantity: c.quantity + 1 } : c);
      }
      return [...prev, { item: product, size: sizeToUse, quantity: 1 }];
    });
    setSelectedProduct(null);
    setIsCartOpen(true);
  };

  const totalItems = cart.reduce((acc, c) => acc + c.quantity, 0);
  const totalPrice = cart.reduce((acc, c) => acc + (c.item.price * c.quantity), 0);
  const freeShippingThreshold = 500;
  const missingForFreeShipping = Math.max(0, freeShippingThreshold - totalPrice);

  return (
    <div className="min-h-screen bg-[#FAFAFA] text-[#111111] font-sans selection:bg-[#111111] selection:text-white">
      
      {/* Top Banner Frete Grátis */}
      <div className="bg-[#111111] text-white text-[11px] font-bold uppercase tracking-widest py-2.5 text-center border-b border-zinc-800">
         <span>✨ FRETE GRÁTIS EM COMPRAS ACIMA DE R$ 500 | PARCELE EM ATÉ 10X SEM JUROS</span>
      </div>

      {/* Header Editorial - STRICT NO SHADOWS */}
      <header className="sticky top-0 z-40 bg-[#FAFAFA]/95 backdrop-blur-md border-b border-zinc-200">
        <div className="max-w-7xl mx-auto px-8 h-20 flex items-center justify-between">
          <div className="flex items-center gap-4">
             <h1 className="text-2xl font-heading font-black text-[#111111] tracking-tight uppercase leading-none">{prospect.trade_name}</h1>
             <span className="text-[10px] bg-zinc-200 text-zinc-800 font-bold px-2.5 py-1 rounded-md uppercase tracking-wider hidden sm:block">Luxury Store</span>
          </div>

          <nav className="hidden md:flex items-center gap-8 text-xs font-bold uppercase tracking-wider text-zinc-600">
             <a href="#produtos" className="hover:text-[#111111] transition-colors">Coleção 2026</a>
             <a href="#provador" className="hover:text-[#111111] transition-colors">Provador Virtual</a>
             <a href="#lookbook" className="hover:text-[#111111] transition-colors">Lookbook & Estilo</a>
          </nav>

          <div className="flex items-center gap-4">
             <button 
                onClick={() => setIsCartOpen(true)}
                className="relative bg-[#111111] text-white px-5 py-2.5 rounded-xl font-bold text-xs uppercase tracking-wider hover:bg-zinc-800 transition-colors flex items-center gap-2 border border-[#111111]"
             >
                <ShoppingBag className="w-4 h-4" />
                <span>Sacola</span>
                {totalItems > 0 && (
                   <span className="bg-[#047857] text-white w-5 h-5 rounded-full text-[10px] font-black flex items-center justify-center">
                      {totalItems}
                   </span>
                )}
             </button>
          </div>
        </div>
      </header>

      {/* Hero Editorial de Moda - STRICT NO SHADOWS */}
      <section className="py-20 px-8 bg-white border-b border-zinc-200">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
           <div className="lg:col-span-6 space-y-6">
              <span className="text-xs font-bold text-[#047857] uppercase tracking-widest">
                 Outono / Inverno 2026
              </span>
              <h2 className="text-5xl lg:text-7xl font-heading font-black text-[#111111] uppercase tracking-tight leading-[1.05]">
                 Sofisticação & <br />
                 <span className="italic font-serif font-normal text-zinc-500">Curadoria Exclusiva.</span>
              </h2>
              <p className="text-sm text-zinc-600 font-light max-w-md leading-relaxed">
                 Calçados e peças de alfaiataria desenvolvidos com matéria-prima nobre e acabamento artesanal.
              </p>

              <div className="pt-4">
                 <a href="#produtos" className="bg-[#111111] text-white px-8 py-4 rounded-xl font-bold text-xs uppercase tracking-wider hover:bg-zinc-800 transition-colors inline-flex items-center gap-3 border border-[#111111]">
                    Ver Nova Coleção <ArrowRight className="w-4 h-4" />
                 </a>
              </div>
           </div>

           <div className="lg:col-span-6">
              <div className="grid grid-cols-2 gap-4">
                 <div className="aspect-[3/4] rounded-xl overflow-hidden border border-zinc-200">
                    <img src={fashionProducts[0].images[0]} alt="Moda 1" className="w-full h-full object-cover" />
                 </div>
                 <div className="aspect-[3/4] rounded-xl overflow-hidden border border-zinc-200 mt-8">
                    <img src={fashionProducts[1].images[0]} alt="Moda 2" className="w-full h-full object-cover" />
                 </div>
              </div>
           </div>
        </div>
      </section>

      {/* Vitrine de Produtos com Filtro - STRICT NO SHADOWS */}
      <section id="produtos" className="py-24 px-8 max-w-7xl mx-auto">
         <div className="flex flex-col md:flex-row md:items-end justify-between mb-12 pb-4 border-b border-zinc-200">
            <div>
               <span className="text-xs font-bold text-[#047857] uppercase tracking-widest">Catálogo Disponível</span>
               <h3 className="text-3xl font-heading font-black uppercase text-[#111111] mt-1">Peças Selecionadas</h3>
            </div>

            <div className="flex gap-2 mt-4 md:mt-0">
               {[
                  { id: 'todos', label: 'Todos os Itens' },
                  { id: 'calcados', label: '👠 Calçados' },
                  { id: 'vestuario', label: '👗 Vestuário' },
                  { id: 'acessorios', label: '👜 Acessórios' },
               ].map(cat => (
                  <button
                     key={cat.id}
                     onClick={() => setActiveCategory(cat.id as any)}
                     className={`px-4 py-2 rounded-xl text-xs font-bold uppercase tracking-wider transition-colors border ${
                        activeCategory === cat.id 
                           ? 'bg-[#111111] text-white border-[#111111]' 
                           : 'bg-white text-zinc-600 border-zinc-300 hover:border-zinc-400'
                     }`}
                  >
                     {cat.label}
                  </button>
               ))}
            </div>
         </div>

         <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {filteredProducts.map(prod => (
               <div 
                  key={prod.id} 
                  onClick={() => { setSelectedProduct(prod); setSelectedSize(prod.sizes[0]); }}
                  className="bg-white rounded-xl border border-zinc-200 overflow-hidden hover:border-zinc-400 transition-all cursor-pointer group flex flex-col justify-between p-3"
               >
                  <div>
                     <div className="relative aspect-[3/4] rounded-lg overflow-hidden mb-4 border border-zinc-100">
                        <img src={prod.images[0]} alt={prod.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                        {prod.badge && (
                           <div className="absolute top-3 left-3 bg-[#111111] text-white text-[10px] font-bold uppercase tracking-widest px-3 py-1 rounded-md">
                              {prod.badge}
                           </div>
                        )}
                     </div>
                     <div className="px-2">
                        <h4 className="font-bold text-base text-[#111111] mb-1 group-hover:text-[#047857] transition-colors">{prod.name}</h4>
                        <p className="text-xs text-zinc-500 font-light leading-relaxed mb-4 line-clamp-2">{prod.description}</p>
                     </div>
                  </div>

                  <div className="px-2 pb-2 pt-2 border-t border-zinc-100 flex items-center justify-between">
                     <div>
                        <span className="text-base font-black text-[#111111]">R$ {prod.price.toFixed(2)}</span>
                        {prod.originalPrice && <span className="text-xs text-zinc-400 line-through ml-2">R$ {prod.originalPrice.toFixed(2)}</span>}
                     </div>
                     <button className="bg-[#111111] text-white p-2 rounded-lg hover:bg-zinc-800 transition-colors">
                        <Plus className="w-4 h-4" />
                     </button>
                  </div>
               </div>
            ))}
         </div>
      </section>

      {/* Provador Virtual Interativo - STRICT NO SHADOWS */}
      <section id="provador" className="py-20 px-8 bg-white border-t border-zinc-200">
         <div className="max-w-4xl mx-auto bg-[#FAFAFA] p-10 rounded-2xl border border-zinc-300">
            <div className="text-center mb-8">
               <span className="text-xs font-bold uppercase tracking-widest text-[#047857] flex items-center justify-center gap-2">
                  <Sparkles className="w-4 h-4" /> Inteligência de Caimento
               </span>
               <h3 className="text-3xl font-heading font-black text-[#111111] uppercase mt-2">Provador Virtual de Tamanho</h3>
               <p className="text-xs text-zinc-500 font-light mt-1">Informe seu peso e altura para recomendarmos o tamanho ideal com 98% de precisão.</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
               <div>
                  <label className="text-xs font-bold text-zinc-700 block mb-2">Sua Altura: {height} cm</label>
                  <input type="range" min="150" max="195" value={height} onChange={e => calculateFitting(Number(e.target.value), weight)} className="w-full accent-[#111111]" />
               </div>
               <div>
                  <label className="text-xs font-bold text-zinc-700 block mb-2">Seu Peso: {weight} kg</label>
                  <input type="range" min="45" max="110" value={weight} onChange={e => calculateFitting(height, Number(e.target.value))} className="w-full accent-[#111111]" />
               </div>
            </div>

            <div className="bg-white p-6 rounded-xl border border-zinc-300 flex items-center justify-between">
               <div>
                  <span className="text-[10px] font-bold text-zinc-400 uppercase tracking-widest block">Tamanho Recomendado para Seu Biotipo</span>
                  <span className="text-3xl font-black text-[#047857]">Tamanho {recommendedSize}</span>
               </div>
               <span className="text-xs font-bold text-zinc-700 bg-zinc-100 px-4 py-2 rounded-lg border border-zinc-200">✓ Caimento Perfeito Garantido</span>
            </div>
         </div>
      </section>

      {/* Cart Drawer Flutuante - STRICT NO SHADOWS */}
      {isCartOpen && (
         <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex justify-end">
            <div className="bg-white w-full max-w-md h-full p-6 flex flex-col justify-between border-l border-zinc-300">
               <div>
                  <div className="flex justify-between items-center pb-6 border-b border-zinc-200 mb-6">
                     <h3 className="font-heading font-black text-lg uppercase tracking-wider flex items-center gap-2">
                        <ShoppingBag className="w-5 h-5 text-[#047857]" /> Sua Sacola de Compras
                     </h3>
                     <button onClick={() => setIsCartOpen(false)} className="text-zinc-400 hover:text-black"><X className="w-6 h-6" /></button>
                  </div>

                  {/* Frete Grátis Progressivo */}
                  <div className="bg-zinc-100 p-4 rounded-xl mb-6 border border-zinc-200">
                     {missingForFreeShipping > 0 ? (
                        <p className="text-xs font-bold text-zinc-700">
                           Faltam <span className="text-[#047857]">R$ {missingForFreeShipping.toFixed(2)}</span> para você ganhar FRETE GRÁTIS!
                        </p>
                     ) : (
                        <p className="text-xs font-bold text-[#047857] flex items-center gap-1.5">
                           <CheckCircle2 className="w-4 h-4" /> Parabéns! Você ganhou FRETE GRÁTIS!
                        </p>
                     )}
                     <div className="w-full h-2 bg-zinc-300 rounded-full mt-2 overflow-hidden">
                        <div className="h-full bg-[#047857]" style={{ width: `${Math.min(100, (totalPrice / freeShippingThreshold) * 100)}%` }}></div>
                     </div>
                  </div>

                  {cart.length === 0 ? (
                     <p className="text-center text-zinc-400 py-12 text-sm font-light">Sua sacola está vazia.</p>
                  ) : (
                     <div className="space-y-4 max-h-[50vh] overflow-y-auto pr-2">
                        {cart.map((c, i) => (
                           <div key={i} className="flex items-center justify-between bg-[#FAFAFA] p-4 rounded-xl border border-zinc-200">
                              <div className="flex items-center gap-3">
                                 <img src={c.item.images[0]} className="w-12 h-14 object-cover rounded border border-zinc-200" alt={c.item.name} />
                                 <div>
                                    <h4 className="font-bold text-xs text-[#111111] leading-tight">{c.item.name}</h4>
                                    <p className="text-[10px] text-zinc-500">Tamanho: {c.size}</p>
                                    <p className="text-xs font-black text-[#047857] mt-1">R$ {(c.item.price * c.quantity).toFixed(2)}</p>
                                 </div>
                              </div>
                              <div className="flex items-center gap-2 bg-white px-2 py-1 rounded-lg border border-zinc-200">
                                 <button onClick={() => setCart(prev => prev.map((item, idx) => idx === i ? { ...item, quantity: Math.max(1, item.quantity - 1) } : item))}><Minus className="w-3 h-3 text-zinc-500" /></button>
                                 <span className="text-xs font-bold">{c.quantity}</span>
                                 <button onClick={() => setCart(prev => prev.map((item, idx) => idx === i ? { ...item, quantity: item.quantity + 1 } : item))}><Plus className="w-3 h-3 text-zinc-500" /></button>
                              </div>
                           </div>
                        ))}
                     </div>
                  )}
               </div>

               {cart.length > 0 && (
                  <div className="pt-6 border-t border-zinc-200">
                     <div className="flex justify-between items-center mb-4">
                        <span className="text-xs text-zinc-500 font-bold uppercase tracking-wider">Subtotal:</span>
                        <span className="text-2xl font-black text-[#111111]">R$ {totalPrice.toFixed(2)}</span>
                     </div>
                     <button className="w-full bg-[#111111] text-white py-4 rounded-xl font-bold uppercase tracking-wider text-xs hover:bg-zinc-800 transition-colors border border-[#111111]">
                        Finalizar Compra via WhatsApp / Pix
                     </button>
                  </div>
               )}
            </div>
         </div>
      )}

      {/* Modal Detalhes do Produto */}
      {selectedProduct && (
         <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl border border-zinc-300 max-w-lg w-full p-8 relative">
               <button onClick={() => setSelectedProduct(null)} className="absolute top-4 right-4 text-zinc-400 hover:text-black font-bold">✕</button>
               <div className="aspect-[4/3] rounded-xl overflow-hidden mb-6 border border-zinc-200">
                  <img src={selectedProduct.images[0]} className="w-full h-full object-cover" alt={selectedProduct.name} />
               </div>
               <h3 className="font-heading font-black text-2xl text-[#111111] mb-2">{selectedProduct.name}</h3>
               <p className="text-xs text-zinc-500 mb-6">{selectedProduct.description}</p>
               
               <div className="mb-6">
                  <span className="text-xs font-bold uppercase tracking-wider text-zinc-700 block mb-2">Selecione o Tamanho:</span>
                  <div className="flex gap-2">
                     {selectedProduct.sizes.map(sz => (
                        <button 
                           key={sz}
                           onClick={() => setSelectedSize(sz)}
                           className={`w-10 h-10 rounded-lg border text-xs font-bold transition-colors ${selectedSize === sz ? 'bg-[#111111] text-white border-[#111111]' : 'bg-zinc-100 text-zinc-700 border-zinc-300'}`}
                        >
                           {sz}
                        </button>
                     ))}
                  </div>
               </div>

               <button 
                  onClick={() => addToCart(selectedProduct)}
                  className="w-full bg-[#111111] text-white py-4 rounded-xl font-bold text-xs uppercase tracking-wider hover:bg-zinc-800 transition-colors"
               >
                  Adicionar à Sacola — R$ {selectedProduct.price.toFixed(2)}
               </button>
            </div>
         </div>
      )}

    </div>
  );
};
